import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import apiClient from '../utils/api';

const ProgramContext = createContext();

export function ProgramProvider({ children }) {
  const [runningProgram, setRunningProgram] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [stepStartTime, setStepStartTime] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [pausedAt, setPausedAt] = useState(null);
  const [totalPausedTime, setTotalPausedTime] = useState(0);
  const intervalRef = useRef(null);

  // Load from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('runningProgram');
    if (saved) {
      try {
        const data = JSON.parse(saved);
        setRunningProgram(data.program);
        setCurrentStep(data.currentStep);
        setStepStartTime(data.stepStartTime);
        setIsPaused(data.isPaused);
        setPausedAt(data.pausedAt);
        setTotalPausedTime(data.totalPausedTime || 0);
      } catch (e) {
        console.error('Error loading saved program:', e);
        localStorage.removeItem('runningProgram');
      }
    }
  }, []);

  // Save to localStorage whenever state changes
  useEffect(() => {
    if (runningProgram && stepStartTime) {
      localStorage.setItem('runningProgram', JSON.stringify({
        program: runningProgram,
        currentStep,
        stepStartTime,
        isPaused,
        pausedAt,
        totalPausedTime,
      }));
    } else if (!runningProgram) {
      localStorage.removeItem('runningProgram');
    }
  }, [runningProgram, currentStep, stepStartTime, isPaused, pausedAt, totalPausedTime]);

  // Calculate time remaining based on timestamp
  useEffect(() => {
    if (!runningProgram || !stepStartTime) return;

    const calculateTimeRemaining = () => {
      const now = Date.now();
      const stepDurationMs = runningProgram.steps[currentStep].duration_minutes * 60 * 1000;
      
      let elapsed;
      if (isPaused && pausedAt) {
        elapsed = pausedAt - stepStartTime - totalPausedTime;
      } else {
        elapsed = now - stepStartTime - totalPausedTime;
      }
      
      const remaining = Math.max(0, Math.floor((stepDurationMs - elapsed) / 1000));
      setTimeRemaining(remaining);

      // Check if step should advance
      if (!isPaused && elapsed >= stepDurationMs) {
        advanceToNextStep();
      }
    };

    calculateTimeRemaining();
    
    if (!isPaused) {
      intervalRef.current = setInterval(calculateTimeRemaining, 1000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [runningProgram, currentStep, stepStartTime, isPaused, pausedAt, totalPausedTime]);

  const advanceToNextStep = () => {
    const nextStepIndex = currentStep + 1;
    
    if (nextStepIndex < runningProgram.steps.length) {
      // Move to next step
      setCurrentStep(nextStepIndex);
      setStepStartTime(Date.now());
      setTotalPausedTime(0);
      setPausedAt(null);
      
      const nextStep = runningProgram.steps[nextStepIndex];
      
      // Activate next frequency
      apiClient.setActiveFrequency(nextStep.frequency_id).catch(console.error);
      
      // Show notification
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification(`${nextStep.frequency_icon} ${nextStep.frequency_name}`, {
          body: `Schritt ${nextStepIndex + 1} von ${runningProgram.steps.length}`,
        });
      }
    } else {
      // Program finished
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification('✅ Programm abgeschlossen', {
          body: `${runningProgram.name} erfolgreich beendet`,
        });
      }
      
      stopProgram();
    }
  };

  const startProgram = async (program) => {
    const startTime = Date.now();
    setRunningProgram(program);
    setCurrentStep(0);
    setStepStartTime(startTime);
    setTimeRemaining(program.steps[0].duration_minutes * 60);
    setIsPaused(false);
    setPausedAt(null);
    setTotalPausedTime(0);
    
    // Activate first frequency
    try {
      await apiClient.setActiveFrequency(program.steps[0].frequency_id);
    } catch (error) {
      console.error('Error activating frequency:', error);
    }
  };

  const stopProgram = async () => {
    setRunningProgram(null);
    setCurrentStep(0);
    setStepStartTime(null);
    setTimeRemaining(0);
    setIsPaused(false);
    setPausedAt(null);
    setTotalPausedTime(0);
    
    // Deactivate frequency
    try {
      await apiClient.setActiveFrequency(null);
    } catch (error) {
      console.error('Error deactivating frequency:', error);
    }
  };

  const togglePause = () => {
    if (isPaused) {
      // Resume
      const pauseDuration = Date.now() - pausedAt;
      setTotalPausedTime(totalPausedTime + pauseDuration);
      setPausedAt(null);
      setIsPaused(false);
    } else {
      // Pause
      setPausedAt(Date.now());
      setIsPaused(true);
    }
  };

  return (
    <ProgramContext.Provider
      value={{
        runningProgram,
        currentStep,
        timeRemaining,
        isPaused,
        startProgram,
        stopProgram,
        togglePause,
      }}
    >
      {children}
    </ProgramContext.Provider>
  );
}

export function useProgram() {
  const context = useContext(ProgramContext);
  if (!context) {
    throw new Error('useProgram must be used within ProgramProvider');
  }
  return context;
}
